import React, { useEffect, useState } from "react";
import {
  FileText,
  ShieldCheck,
  UserCheck,
  Home,
  CreditCard,
  Ban,
  Scale,
  Phone,
  ChevronRight,
} from "lucide-react";

const sections = [
  {
    id: "introduction",
    title: "Introduction",
    icon: FileText,
  },
  {
    id: "terms",
    title: "Terms & Conditions",
    icon: ShieldCheck,
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    icon: UserCheck,
  },
  {
    id: "listings",
    title: "Listings",
    icon: Home,
  },
  {
    id: "payments",
    title: "Payments",
    icon: CreditCard,
  },
  {
    id: "prohibited",
    title: "Prohibited Activities",
    icon: Ban,
  },
  {
    id: "liability",
    title: "Liability",
    icon: Scale,
  },
  {
    id: "contact",
    title: "Contact",
    icon: Phone,
  },
];

export default function TermsConditionsPage() {
  const [activeSection, setActiveSection] = useState("introduction");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((e) => e.isIntersecting);

        if (visible) {
          setActiveSection(visible.target.id);
        }
      },
      {
        threshold: 0.35,
      }
    );

    sections.forEach((section) => {
      const el = document.getElementById(section.id);

      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* HERO */}

      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">

        <div className="max-w-7xl mx-auto px-6 py-6">

          <div className="max-w-3xl">

            {/* <span className="inline-flex items-center bg-white/20 rounded-full px-4 py-1 text-sm font-medium mb-5">

              RoomVetiyo Legal

            </span> */}

            <h1 className="text-3xl font-bold leading-tight">

              Terms & Conditions

              <br />

              <span className="text-orange-100">

                & Privacy Policy

              </span>

            </h1>

            <p className="mt-4 text-lg text-orange-100 leading-8">

              Welcome to RoomVetiyo.

              Please read these Terms & Conditions and Privacy Policy carefully
              before using our platform.

            </p>

            <div className="mt-4 flex gap-4">

              <span className="bg-white text-orange-600 px-5 py-2 rounded-full font-semibold">

                Version 1.0

              </span>

              <span className="bg-white/20 px-5 py-2 rounded-full">

                Last Updated: July 2026

              </span>

            </div>

          </div>

        </div>

      </div>

      {/* BODY */}

      <div className="max-w-7xl mx-auto px-6 py-4">

        <div className="grid lg:grid-cols-12 gap-8">

          {/* SIDEBAR */}

          <aside className="lg:col-span-3">

            <div className="sticky top-24">

              <div className="bg-white rounded-3xl shadow-sm border p-6">

                <h3 className="font-bold text-lg mb-6">

                  On this page

                </h3>

                <div className="space-y-2">

                  {sections.map((section) => {

                    const Icon = section.icon;

                    const active =
                      activeSection === section.id;

                    return (

                      <button
                        key={section.id}
                        onClick={() =>
                          document
                            .getElementById(section.id)
                            ?.scrollIntoView({
                              behavior: "smooth",
                            })
                        }
                        className={`w-full flex items-center justify-between rounded-xl px-4 py-3 transition

                        ${
                          active
                            ? "bg-orange-500 text-white"
                            : "hover:bg-orange-50 text-gray-700"
                        }`}
                      >

                        <div className="flex items-center gap-3">

                          <Icon size={18} />

                          <span className="font-medium">

                            {section.title}

                          </span>

                        </div>

                        <ChevronRight size={16} />

                      </button>

                    );

                  })}

                </div>

              </div>

              {/* HELP CARD */}

              <div className="bg-orange-50 rounded-3xl border border-orange-100 p-6 mt-6">

                <h3 className="font-bold text-lg">

                  Need Help?

                </h3>

                <p className="text-gray-600 text-sm mt-3 leading-6">

                  If you have any questions about our legal policies,
                  please contact the RoomVetiyo support team.

                </p>

                <button className="mt-5 w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl py-3 font-semibold transition">

                  Contact Support

                </button>

              </div>

            </div>

          </aside>

          {/* CONTENT */}

          <main className="lg:col-span-9 space-y-6">

            {/* Introduction */}

            <section
              id="introduction"
              className="bg-white rounded-3xl border shadow-sm p-6"
            >

              <div className="flex items-center gap-4 mb-5">

                <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center">

                  <FileText className="text-orange-600" />

                </div>

                <div>

                  <h2 className="text-2xl font-bold">

                    Introduction

                  </h2>

                  <p className="text-gray-500">

                    Welcome to RoomVetiyo

                  </p>

                </div>

              </div>

              <p className="text-gray-700 leading-8">

                Welcome to <strong>RoomVetiyo</strong>, Nepal's room rental
                marketplace.

                By accessing or using our platform, you agree to comply with
                these Terms & Conditions and our Privacy Policy.

                Please read them carefully before using our services.

              </p>

            </section>

            {/* ========================================= */}
            {/* TERMS & CONDITIONS */}
            {/* ========================================= */}

            <section
              id="terms"
              className="bg-white rounded-3xl border shadow-sm p-6"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center">
                  <ShieldCheck className="text-orange-600" />
                </div>

                <div>
                  <h2 className="text-2xl font-bold">
                    Terms & Conditions
                  </h2>

                  <p className="text-gray-500">
                    Conditions for using RoomVetiyo
                  </p>
                </div>
              </div>

              <div className="space-y-6">

                <div className="border rounded-2xl p-6">
                  <h3 className="font-semibold text-lg mb-3">
                    Acceptance of Terms
                  </h3>

                  <p className="text-gray-700 leading-8">
                    By registering an account, browsing listings,
                    posting rooms, or using any feature of RoomVetiyo,
                    you agree to be legally bound by these Terms &
                    Conditions and our Privacy Policy.
                  </p>
                </div>

                <div className="border rounded-2xl p-6">
                  <h3 className="font-semibold text-lg mb-3">
                    Our Platform
                  </h3>

                  <p className="text-gray-700 leading-8">
                    RoomVetiyo is an online room rental platform that
                    connects property owners with room seekers across
                    Nepal. We provide a marketplace only and are not
                    responsible for agreements made between users.
                  </p>
                </div>

                <div className="border rounded-2xl p-6">
                  <h3 className="font-semibold text-lg mb-3">
                    User Responsibilities
                  </h3>

                  <div className="grid md:grid-cols-2 gap-4">

                    <div className="flex gap-3">
                      <span className="text-green-600 text-xl">✔</span>
                      <span>
                        Provide accurate and truthful information.
                      </span>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-green-600 text-xl">✔</span>
                      <span>
                        Keep your account credentials secure.
                      </span>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-green-600 text-xl">✔</span>
                      <span>
                        Follow all applicable laws and regulations.
                      </span>
                    </div>

                    <div className="flex gap-3">
                      <span className="text-green-600 text-xl">✔</span>
                      <span>
                        Respect other users and communicate politely.
                      </span>
                    </div>

                  </div>
                </div>

              </div>
            </section>



            {/* ========================================= */}
            {/* PRIVACY POLICY */}
            {/* ========================================= */}

            <section
              id="privacy"
              className="bg-white rounded-3xl border shadow-sm p-6"
            >

              <div className="flex items-center gap-4 mb-6">

                <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">

                  <UserCheck className="text-blue-600" />

                </div>

                <div>

                  <h2 className="text-2xl font-bold">

                    Privacy Policy

                  </h2>

                  <p className="text-gray-500">

                    How we collect and use your information

                  </p>

                </div>

              </div>

              <div className="space-y-6">

                <div className="border rounded-2xl p-6">

                  <h3 className="font-semibold text-lg mb-3">
                    Information We Collect
                  </h3>

                  <ul className="space-y-2 text-gray-700">

                    <li>• Full name</li>

                    <li>• Email address</li>

                    <li>• Phone number</li>

                    <li>• Property information</li>

                    <li>• Messages exchanged on the platform</li>

                    <li>• Device and browser information</li>

                  </ul>

                </div>

                <div className="border rounded-2xl p-6">

                  <h3 className="font-semibold text-lg mb-3">
                    How We Use Your Information
                  </h3>

                  <div className="grid md:grid-cols-2 gap-4">

                    <div className="flex gap-3">
                      <span>✅</span>
                      <span>Create your account</span>
                    </div>

                    <div className="flex gap-3">
                      <span>✅</span>
                      <span>Display room listings</span>
                    </div>

                    <div className="flex gap-3">
                      <span>✅</span>
                      <span>Improve recommendations</span>
                    </div>

                    <div className="flex gap-3">
                      <span>✅</span>
                      <span>Customer support</span>
                    </div>

                    <div className="flex gap-3">
                      <span>✅</span>
                      <span>Platform security</span>
                    </div>

                    <div className="flex gap-3">
                      <span>✅</span>
                      <span>Fraud prevention</span>
                    </div>

                  </div>

                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">

                  <h3 className="font-semibold mb-3">
                    Your Privacy Matters
                  </h3>

                  <p className="text-gray-700 leading-8">

                    We do not sell your personal information.
                    Your data is used only for providing and improving
                    RoomVetiyo services.

                  </p>

                </div>

              </div>

            </section>



            {/* ========================================= */}
            {/* LISTINGS */}
            {/* ========================================= */}

            <section
              id="listings"
              className="bg-white rounded-3xl border shadow-sm p-6"
            >

              <div className="flex items-center gap-4 mb-6">

                <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center">

                  <Home className="text-green-600" />

                </div>

                <div>

                  <h2 className="text-2xl font-bold">
                    Property Listings
                  </h2>

                  <p className="text-gray-500">
                    Responsibilities of property owners
                  </p>

                </div>

              </div>

              <div className="grid md:grid-cols-2 gap-6">

                <div className="border rounded-2xl p-6">

                  <h3 className="font-semibold mb-4">
                    Owners Must
                  </h3>

                  <ul className="space-y-3 text-gray-700">

                    <li>✔ Upload genuine room photos</li>

                    <li>✔ Provide accurate rent information</li>

                    <li>✔ Mention available facilities</li>

                    <li>✔ Keep listing updated</li>

                    <li>✔ Respond honestly to seekers</li>

                  </ul>

                </div>

                <div className="bg-orange-50 border border-orange-100 rounded-2xl p-6">

                  <h3 className="font-semibold mb-4">
                    RoomVetiyo May
                  </h3>

                  <ul className="space-y-3 text-gray-700">

                    <li>• Review listings before publishing</li>

                    <li>• Reject fake listings</li>

                    <li>• Suspend accounts violating policies</li>

                    <li>• Remove misleading information</li>

                    <li>• Disable fraudulent listings</li>

                  </ul>

                </div>

              </div>

            </section>



            {/* ========================================= */}
            {/* PAYMENTS */}
            {/* ========================================= */}

            <section
              id="payments"
              className="bg-white rounded-3xl border shadow-sm p-6"
            >

              <div className="flex items-center gap-4 mb-6">

                <div className="w-14 h-14 rounded-2xl bg-yellow-100 flex items-center justify-center">

                  <CreditCard className="text-yellow-600" />

                </div>

                <div>

                  <h2 className="text-2xl font-bold">
                    Booking & Payments
                  </h2>

                  <p className="text-gray-500">
                    Payment responsibilities
                  </p>

                </div>

              </div>

              <div className="bg-yellow-50 rounded-2xl border border-yellow-100 p-4">

                <p className="leading-8 text-gray-700">

                  RoomVetiyo does not directly handle rent,
                  deposits or agreements unless explicitly
                  stated. All financial transactions are
                  conducted between the room owner and the
                  tenant. Users are responsible for verifying
                  payment details before making any transfer.

                </p>

              </div>

            </section>

            {/* ========================================= */}
            {/* PROHIBITED ACTIVITIES */}
            {/* ========================================= */}

            <section
              id="prohibited"
              className="bg-white rounded-3xl border shadow-sm p-6"
            >

              <div className="flex items-center gap-4 mb-6">

                <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">

                  <Ban className="text-red-600" />

                </div>

                <div>

                  <h2 className="text-2xl font-bold">
                    Prohibited Activities
                  </h2>

                  <p className="text-gray-500">
                    Activities that are not allowed on RoomVetiyo
                  </p>

                </div>

              </div>

              <div className="grid md:grid-cols-2 gap-5">

                {[
                  "Posting fake or misleading room listings.",
                  "Providing false contact information.",
                  "Uploading copyrighted or offensive content.",
                  "Harassing, threatening or abusing other users.",
                  "Using the platform for illegal activities.",
                  "Attempting to hack or disrupt the platform.",
                  "Creating duplicate or fraudulent accounts.",
                  "Sending spam or unwanted advertisements.",
                ].map((item, index) => (

                  <div
                    key={index}
                    className="flex gap-4 p-2 border rounded-2xl bg-red-50 border-red-100"
                  >

                    <div className="text-red-600 text-xl">
                      ✖
                    </div>

                    <p className="text-gray-700">
                      {item}
                    </p>

                  </div>

                ))}

              </div>

            </section>



            {/* ========================================= */}
            {/* LIABILITY */}
            {/* ========================================= */}

            <section
              id="liability"
              className="bg-white rounded-3xl border shadow-sm p-6"
            >

              <div className="flex items-center gap-4 mb-3">

                <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">

                  <Scale className="text-purple-600" />

                </div>

                <div>

                  <h2 className="text-2xl font-bold">
                    Limitation of Liability
                  </h2>

                  <p className="text-gray-500">
                    Our responsibility as a platform
                  </p>

                </div>

              </div>

              <div className="bg-purple-50 rounded-2xl border border-purple-100 p-4">

                <p className="leading-8 text-gray-700">

                  RoomVetiyo acts solely as a marketplace
                  connecting property owners and room seekers.

                  We do not own, inspect, rent or manage
                  properties listed on the platform.

                  Users are responsible for verifying
                  ownership, rent amount, facilities,
                  agreements and payments before making
                  any decision.

                </p>

              </div>

            </section>



            {/* ========================================= */}
            {/* GOVERNING LAW */}
            {/* ========================================= */}

            <section
              className="bg-white rounded-3xl border shadow-sm p-6"
            >

              <h2 className="text-2xl font-bold mb-2">
                Governing Law
              </h2>

              <p className="leading-8 text-gray-700">

                These Terms & Conditions shall be governed
                and interpreted according to the laws of Nepal.

                Any dispute arising from the use of RoomVetiyo
                shall be subject to the jurisdiction of the
                competent courts of Nepal.

              </p>

            </section>



            {/* ========================================= */}
            {/* CONTACT */}
            {/* ========================================= */}

            <section
              id="contact"
              className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-3xl shadow-sm p-6 text-white"
            >

              <div className="grid lg:grid-cols-2 gap-10">

                <div>

                  <h2 className="text-4xl font-bold">

                    Contact Us

                  </h2>

                  <p className="mt-5 text-orange-100 leading-8">

                    If you have any questions regarding
                    these Terms & Conditions or our
                    Privacy Policy, please contact us.

                  </p>

                </div>

                <div className="space-y-5">

                  <div className="bg-white/10 rounded-2xl p-5">

                    <h4 className="font-semibold">
                      📧 Email
                    </h4>

                    <p className="mt-2">
                      support@roomvetiyo.com
                    </p>

                  </div>

                  <div className="bg-white/10 rounded-2xl p-5">

                    <h4 className="font-semibold">
                      📞 Phone
                    </h4>

                    <p className="mt-2">
                      +977-9708919809
                    </p>

                  </div>

                  <div className="bg-white/10 rounded-2xl p-5">

                    <h4 className="font-semibold">
                      📍 Address
                    </h4>

                    <p className="mt-2">

                      Kathmandu,
                      Nepal

                    </p>

                  </div>

                </div>

              </div>

            </section>



            {/* ========================================= */}
            {/* FOOTER */}
            {/* ========================================= */}

            {/* <div className="bg-white rounded-3xl border shadow-sm p-8">

              <div className="flex flex-col lg:flex-row justify-between items-center gap-6">

                <div>

                  <h3 className="font-bold text-xl">

                    RoomVetiyo

                  </h3>

                  <p className="text-gray-500 mt-2">

                    Connecting room owners and seekers
                    across Nepal.

                  </p>

                </div>

                <div className="flex gap-6 text-sm">

                  <a
                    href="#terms"
                    className="hover:text-orange-500"
                  >
                    Terms
                  </a>

                  <a
                    href="#privacy"
                    className="hover:text-orange-500"
                  >
                    Privacy
                  </a>

                  <a
                    href="#contact"
                    className="hover:text-orange-500"
                  >
                    Contact
                  </a>

                </div>

              </div>

              <div className="border-t mt-8 pt-6 text-center text-gray-500 text-sm">

                © {new Date().getFullYear()} RoomVetiyo.

                All Rights Reserved.

              </div>

            </div> */}

          </main>

        </div>

      </div>

    </div>

  );
}
